import type { ThreeEvent } from '@react-three/fiber'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { DoubleSide, Plane, Vector3 } from 'three'
import {
  pontoDaAbertura,
  semieixos,
  type AberturaTanque,
  type PerfilCaminhaoTanque,
} from '../../dominio/caminhaoTanque/perfil'
import { PREFIXO_SELECIONAVEL } from '../camera/comandosCamera'
import { AcabamentoCarreta3D } from './AcabamentoCarreta3D'
import { Cabine3D } from './Cabine3D'
import { Roda3D } from './Roda3D'
import {
  texturaPainelSeguranca,
  texturaRotuloRisco,
} from './texturasSinalizacao'

export type ParteCaminhao =
  | { tipo: 'cavalo' }
  | { tipo: 'tanque' }
  | { tipo: 'painel' }
  | { tipo: 'rotulo'; classe: string }
  | { tipo: 'abertura'; id: string }

/** Identidade estável de uma parte, para seleção e enquadramento (`selecionavel:<chave>`). */
export function chaveParte(parte: ParteCaminhao): string {
  switch (parte.tipo) {
    case 'rotulo':
      return `rotulo-${parte.classe}`
    case 'abertura':
      return `abertura-${parte.id}`
    default:
      return parte.tipo
  }
}

export interface PropsCaminhaoTanque3D {
  perfil: PerfilCaminhaoTanque
  numeroRisco: string
  onu: number
  /** Classe principal primeiro, depois os riscos subsidiários. */
  classes: readonly string[]
  mostrarSinalizacao?: boolean
  chaveSelecionada?: string | null
  aoSelecionar?: (parte: ParteCaminhao) => void
  /** Abaixo de 1 deixa ver o nível de líquido (transbordo). */
  opacidadeTanque?: number
  /**
   * Altura da superfície do líquido no MUNDO, m. O líquido é o próprio volume
   * do tanque recortado por um plano horizontal — por isso continua certo com
   * o caminhão tombado ou capotado.
   */
  nivelLiquidoMundoY?: number | null
  corLiquido?: string
  corCavalo?: string
  /** Conteúdo extra no referencial do tanque (nível de líquido, mangotes). */
  children?: ReactNode
}

const COR_DESTAQUE = '#ffb347'

/**
 * Caminhão-tanque procedural: cavalo mecânico, semirreboque com tanque
 * cilíndrico (gás pressurizado) ou elíptico (líquido), compartimentos, bocas
 * de visita, válvulas, painel de segurança e rótulos de risco.
 *
 * Tudo sai do perfil de dimensões. O tanque é desenhado com um cilindro de
 * raio 1 escalado nos dois semieixos, para que o circular e o elíptico sejam o
 * mesmo código.
 */
export function CaminhaoTanque3D({
  perfil,
  numeroRisco,
  onu,
  classes,
  mostrarSinalizacao = true,
  chaveSelecionada = null,
  aoSelecionar,
  opacidadeTanque = 1,
  nivelLiquidoMundoY = null,
  corLiquido = '#2f7fd8',
  corCavalo = '#c8ccd2',
  children,
}: PropsCaminhaoTanque3D) {
  const { cavalo, tanque } = perfil
  const [a, b] = semieixos(tanque)
  const zFrenteTanque = tanque.zCentroM + tanque.comprimentoM / 2
  const zTrasTanque = tanque.zCentroM - tanque.comprimentoM / 2
  const zFrenteCavalo = cavalo.zCentroM + cavalo.comprimentoM / 2

  const planosLiquido = useMemo(
    () =>
      nivelLiquidoMundoY == null
        ? []
        : [new Plane(new Vector3(0, -1, 0), nivelLiquidoMundoY)],
    [nivelLiquidoMundoY],
  )

  const texturaPainel = useMemo(
    () =>
      mostrarSinalizacao ? texturaPainelSeguranca(numeroRisco, onu) : null,
    [mostrarSinalizacao, numeroRisco, onu],
  )
  const texturasRotulo = useMemo(
    () =>
      mostrarSinalizacao
        ? classes.map((classe) => ({
            classe,
            textura: texturaRotuloRisco(classe),
          }))
        : [],
    [mostrarSinalizacao, classes],
  )
  useEffect(() => () => texturaPainel?.dispose(), [texturaPainel])
  useEffect(
    () => () => texturasRotulo.forEach(({ textura }) => textura?.dispose()),
    [texturasRotulo],
  )

  const [chaveSobre, setChaveSobre] = useState<string | null>(null)
  useEffect(() => {
    document.body.style.cursor = chaveSobre && aoSelecionar ? 'pointer' : ''
    return () => {
      document.body.style.cursor = ''
    }
  }, [chaveSobre, aoSelecionar])

  const interativo = (parte: ParteCaminhao) => {
    const chave = chaveParte(parte)
    return {
      name: `${PREFIXO_SELECIONAVEL}${chave}`,
      onClick: (evento: ThreeEvent<MouseEvent>) => {
        if (!aoSelecionar) return
        evento.stopPropagation()
        aoSelecionar(parte)
      },
      onPointerOver: (evento: ThreeEvent<PointerEvent>) => {
        evento.stopPropagation()
        setChaveSobre(chave)
      },
      onPointerOut: () =>
        setChaveSobre((atual) => (atual === chave ? null : atual)),
    }
  }
  const realce = (parte: ParteCaminhao) => {
    const chave = chaveParte(parte)
    if (chave === chaveSelecionada)
      return { emissive: COR_DESTAQUE, emissiveIntensity: 0.3 }
    if (chave === chaveSobre && aoSelecionar)
      return { emissive: COR_DESTAQUE, emissiveIntensity: 0.15 }
    return { emissive: '#000000', emissiveIntensity: 0 }
  }

  const transparente = opacidadeTanque < 1
  const eixosCavalo = [
    zFrenteCavalo - 1.25,
    cavalo.zCentroM - cavalo.comprimentoM / 2 + 1.6,
    cavalo.zCentroM - cavalo.comprimentoM / 2 + 0.3,
  ]
  const eixosCarreta = [zTrasTanque + 2.6, zTrasTanque + 1.3, zTrasTanque]

  // Painel na frente do cavalo, na traseira do tanque e nas duas laterais; rótulo na traseira e nas laterais.
  const posicoesPainel: Array<{
    posicao: [number, number, number]
    rotacaoY: number
  }> = [
    // à frente do para-choque (que avança 0,175 m), senão o painel fica escondido atrás dele
    { posicao: [0, 0.98, zFrenteCavalo + 0.2], rotacaoY: 0 },
    {
      posicao: [
        0,
        tanque.alturaEixoM - b * 0.35,
        zTrasTanque - tanque.profundidadeTampoM - 0.32,
      ],
      rotacaoY: Math.PI,
    },
    {
      posicao: [a + 0.04, tanque.alturaEixoM - b * 0.45, tanque.zCentroM + 1.2],
      rotacaoY: Math.PI / 2,
    },
    {
      posicao: [
        -a - 0.04,
        tanque.alturaEixoM - b * 0.45,
        tanque.zCentroM + 1.2,
      ],
      rotacaoY: -Math.PI / 2,
    },
  ]
  const posicoesRotulo = (
    indice: number,
  ): Array<{ posicao: [number, number, number]; rotacaoY: number }> => {
    const deslocamento = indice * 0.55
    return [
      {
        posicao: [
          0.5 + deslocamento,
          tanque.alturaEixoM - b * 0.35,
          zTrasTanque - tanque.profundidadeTampoM - 0.32,
        ],
        rotacaoY: Math.PI,
      },
      {
        posicao: [
          a + 0.04,
          tanque.alturaEixoM - b * 0.45,
          tanque.zCentroM + 0.2 - deslocamento,
        ],
        rotacaoY: Math.PI / 2,
      },
      {
        posicao: [
          -a - 0.04,
          tanque.alturaEixoM - b * 0.45,
          tanque.zCentroM + 0.2 - deslocamento,
        ],
        rotacaoY: -Math.PI / 2,
      },
    ]
  }

  return (
    <group>
      {/* --- Cavalo mecânico ------------------------------------------------ */}
      <group {...interativo({ tipo: 'cavalo' })}>
        <Cabine3D
          perfil={cavalo}
          cor={corCavalo}
          realce={realce({ tipo: 'cavalo' })}
        />
      </group>

      {/* --- Semirreboque -------------------------------------------------- */}
      <mesh
        position={[0, tanque.alturaEixoM - b - 0.18, tanque.zCentroM]}
        castShadow
      >
        <boxGeometry args={[1.0, 0.25, tanque.comprimentoM]} />
        <meshStandardMaterial color="#23272c" roughness={0.7} />
      </mesh>

      <group {...interativo({ tipo: 'tanque' })}>
        {/* costado */}
        <mesh
          position={[0, tanque.alturaEixoM, tanque.zCentroM]}
          rotation={[Math.PI / 2, 0, 0]}
          scale={[a, 1, b]}
          castShadow={!transparente}
          receiveShadow
        >
          <cylinderGeometry args={[1, 1, tanque.comprimentoM, 48, 1, true]} />
          <meshStandardMaterial
            color={tanque.secao === 'circular' ? '#e6ecef' : '#c1cbd2'}
            metalness={tanque.secao === 'circular' ? 0.4 : 0.82}
            roughness={tanque.secao === 'circular' ? 0.3 : 0.24}
            transparent={transparente}
            opacity={opacidadeTanque}
            depthWrite={!transparente}
            side={DoubleSide}
            {...realce({ tipo: 'tanque' })}
          />
        </mesh>
        {/* tampos */}
        {[1, -1].map((sentido) => (
          <mesh
            key={sentido}
            position={[
              0,
              tanque.alturaEixoM,
              tanque.zCentroM + (sentido * tanque.comprimentoM) / 2,
            ]}
            scale={[a, b, tanque.profundidadeTampoM]}
            castShadow={!transparente}
          >
            {/* meia esfera pelo azimute: phi em [0, π] tem z ≥ 0 (tampo dianteiro), [π, 2π] o traseiro */}
            <sphereGeometry
              args={[1, 40, 20, sentido > 0 ? 0 : Math.PI, Math.PI, 0, Math.PI]}
            />
            <meshStandardMaterial
              color={tanque.secao === 'circular' ? '#e6ecef' : '#c1cbd2'}
              metalness={tanque.secao === 'circular' ? 0.4 : 0.82}
              roughness={tanque.secao === 'circular' ? 0.3 : 0.24}
              transparent={transparente}
              opacity={opacidadeTanque}
              depthWrite={!transparente}
              side={DoubleSide}
              {...realce({ tipo: 'tanque' })}
            />
          </mesh>
        ))}
        {nivelLiquidoMundoY != null && (
          <mesh
            position={[0, tanque.alturaEixoM, tanque.zCentroM]}
            rotation={[Math.PI / 2, 0, 0]}
            scale={[a * 0.97, 1, b * 0.97]}
            raycast={() => null}
          >
            <cylinderGeometry
              args={[1, 1, tanque.comprimentoM + tanque.profundidadeTampoM, 48]}
            />
            <meshStandardMaterial
              color={corLiquido}
              transparent
              opacity={0.75}
              roughness={0.2}
              clippingPlanes={planosLiquido}
              side={DoubleSide}
            />
          </mesh>
        )}
        {/* cintas entre compartimentos */}
        {Array.from({ length: tanque.compartimentos + 1 }, (_, i) => {
          const z =
            zTrasTanque + (tanque.comprimentoM * i) / tanque.compartimentos
          return (
            <mesh
              key={i}
              position={[0, tanque.alturaEixoM, z]}
              rotation={[Math.PI / 2, 0, 0]}
              scale={[a + 0.025, 1, b + 0.025]}
            >
              <cylinderGeometry args={[1, 1, 0.08, 48, 1, true]} />
              <meshStandardMaterial
                color="#8a939c"
                metalness={0.6}
                roughness={0.35}
                side={DoubleSide}
              />
            </mesh>
          )
        })}
      </group>

      {/* passarela no topo do tanque atmosférico */}
      {tanque.secao === 'eliptica' && (
        <mesh position={[0, tanque.alturaEixoM + b + 0.05, tanque.zCentroM]}>
          <boxGeometry args={[0.5, 0.04, tanque.comprimentoM * 0.92]} />
          <meshStandardMaterial
            color="#5b636c"
            metalness={0.5}
            roughness={0.6}
          />
        </mesh>
      )}

      {/* para-choque traseiro */}
      <mesh
        position={[0, 0.6, zTrasTanque - tanque.profundidadeTampoM - 0.3]}
        castShadow
      >
        <boxGeometry args={[2.3, 0.18, 0.15]} />
        <meshStandardMaterial color="#2b2f35" />
      </mesh>

      <AcabamentoCarreta3D tanque={tanque} />

      {/* --- Aberturas ------------------------------------------------------ */}
      {tanque.aberturas.map((abertura) => (
        <Abertura3D
          key={abertura.id}
          abertura={abertura}
          ponto={pontoDaAbertura(tanque, abertura)}
          propsInterativos={interativo({ tipo: 'abertura', id: abertura.id })}
          realce={realce({ tipo: 'abertura', id: abertura.id })}
        />
      ))}

      {/* --- Rodas ---------------------------------------------------------- */}
      {[...eixosCavalo, ...eixosCarreta].map((z, i) =>
        [1, -1].map((lado) => (
          <Roda3D key={`${i}-${lado}`} lado={lado} z={z} />
        )),
      )}

      {/* --- Sinalização ---------------------------------------------------- */}
      {mostrarSinalizacao &&
        posicoesPainel.map(({ posicao, rotacaoY }, i) => (
          <mesh
            key={`painel-${i}`}
            position={posicao}
            rotation={[0, rotacaoY, 0]}
            {...interativo({ tipo: 'painel' })}
          >
            <planeGeometry args={[0.4, 0.3]} />
            <meshStandardMaterial
              map={texturaPainel ?? undefined}
              color={texturaPainel ? '#ffffff' : '#f07c00'}
              roughness={0.6}
              {...realce({ tipo: 'painel' })}
            />
          </mesh>
        ))}
      {texturasRotulo.map(({ classe, textura }, indice) =>
        posicoesRotulo(indice).map(({ posicao, rotacaoY }, i) => (
          <mesh
            key={`rotulo-${classe}-${i}`}
            position={posicao}
            rotation={[0, rotacaoY, 0]}
            {...interativo({ tipo: 'rotulo', classe })}
          >
            <planeGeometry args={[0.42, 0.42]} />
            <meshStandardMaterial
              map={textura ?? undefined}
              transparent
              alphaTest={0.5}
              roughness={0.6}
              {...realce({ tipo: 'rotulo', classe })}
            />
          </mesh>
        )),
      )}

      {children}
    </group>
  )
}

const CORES_ABERTURA: Record<AberturaTanque['tipo'], string> = {
  valvulaFaseLiquida: '#2f7fd8',
  valvulaFaseVapor: '#e7c13c',
  valvulaFundo: '#c2412d',
  valvulaAlivio: '#d23c3c',
  bocaVisita: '#7c8791',
}

function Abertura3D({
  abertura,
  ponto,
  propsInterativos,
  realce,
}: {
  abertura: AberturaTanque
  ponto: readonly [number, number, number]
  propsInterativos: object
  realce: { emissive: string; emissiveIntensity: number }
}) {
  const [x, y, z] = ponto
  const cor = CORES_ABERTURA[abertura.tipo]
  const material = (
    <meshStandardMaterial
      color={cor}
      metalness={0.5}
      roughness={0.4}
      {...realce}
    />
  )

  if (abertura.tipo === 'bocaVisita') {
    return (
      <group {...propsInterativos}>
        <mesh position={[x, y + 0.07, z]} castShadow>
          <cylinderGeometry args={[0.32, 0.34, 0.14, 28]} />
          {material}
        </mesh>
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i * Math.PI) / 4
          return (
            <mesh
              key={i}
              position={[
                x + Math.cos(a) * 0.27,
                y + 0.155,
                z + Math.sin(a) * 0.27,
              ]}
            >
              <cylinderGeometry args={[0.024, 0.024, 0.028, 6]} />
              <meshStandardMaterial
                color="#c4cdd0"
                metalness={0.75}
                roughness={0.35}
                {...realce}
              />
            </mesh>
          )
        })}
        <mesh position={[x, y + 0.2, z]}>
          <boxGeometry args={[0.2, 0.035, 0.035]} />
          <meshStandardMaterial color="#b5c2c8" metalness={0.65} {...realce} />
        </mesh>
      </group>
    )
  }
  if (abertura.tipo === 'valvulaAlivio') {
    return (
      <group {...propsInterativos}>
        <mesh position={[x, y + 0.18, z]} castShadow>
          <cylinderGeometry args={[0.08, 0.1, 0.36, 16]} />
          {material}
        </mesh>
        <mesh position={[x, y + 0.4, z]}>
          <cylinderGeometry args={[0.12, 0.12, 0.06, 16]} />
          {material}
        </mesh>
      </group>
    )
  }
  // Captação no fundo ganha um tubo de descida até o corpo da válvula; a de topo
  // fica sobre o costado. O desenho marca onde a abertura toca o produto.
  const noTopo = abertura.anguloRad === 0
  const yComando = noTopo ? y - 0.05 : y - 0.25
  return (
    <group {...propsInterativos}>
      {!noTopo && (
        <mesh position={[x, y - 0.12, z]}>
          <cylinderGeometry args={[0.06, 0.06, 0.24, 12]} />
          <meshStandardMaterial
            color="#555c63"
            metalness={0.6}
            roughness={0.4}
          />
        </mesh>
      )}
      <mesh position={[x, yComando, z + 0.185]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.095, 0.095, 0.16, 12]} />
        <meshStandardMaterial
          color="#9dabb2"
          metalness={0.7}
          roughness={0.35}
          {...realce}
        />
      </mesh>
      <mesh position={[x, yComando, z + 0.275]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.122, 0.122, 0.035, 12]} />
        <meshStandardMaterial
          color="#617985"
          metalness={0.65}
          roughness={0.4}
          {...realce}
        />
      </mesh>
      <mesh position={[x, yComando + 0.19, z]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.115, 0.018, 6, 14]} />
        {material}
      </mesh>
      {[0, Math.PI / 2].map((a) => (
        <mesh key={a} position={[x, yComando + 0.19, z]} rotation={[0, a, 0]}>
          <boxGeometry args={[0.23, 0.018, 0.018]} />
          {material}
        </mesh>
      ))}
      <mesh position={[x, yComando + 0.13, z]}>
        <cylinderGeometry args={[0.024, 0.024, 0.12, 8]} />
        <meshStandardMaterial color="#aab9c0" metalness={0.7} {...realce} />
      </mesh>
      <mesh position={[x, yComando, z]} castShadow>
        <boxGeometry args={[0.26, 0.2, 0.26]} />
        {material}
      </mesh>
    </group>
  )
}
